import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { StoreService } from 'src/app/store.service';
import { NgbModal, ModalDismissReasons } from '@ng-bootstrap/ng-bootstrap';
import { Router } from '@angular/router';
import { UtilitiesService } from 'src/app/core/utilities.service';
import { StoreInfoService } from 'src/app/store-info.service';

@Component({
  selector: 'app-list-stores',
  templateUrl: './pages.component.html',
  styleUrls: ['./pages.component.scss'],
})
export class PagesComponent implements OnInit {
  constructor(
    private svc: StoreService,
    private modalService: NgbModal,
    public utils: UtilitiesService,
    private router: Router,
    private storeInfoService: StoreInfoService
  ) {}
  public bearer: string;
  public storeId: string;
  public pageList = [];
  public loading = true;
  public closeResult = '';
  public success = 'success';

  public pagePaginationList = [];
  public displayedPageList = [];
  public page = 1;
  public selectedPage = 10;
  public start = 0;
  public end = 0;
  public expanded = [];

  public duplicatePageName: string;
  public pageName: string;
  /** Set with pageName: the delete dialog warns before giving up the home route. */
  public deletingHomePage = false;
  public filteredString: string;

  private storeUrl = '';
  private previewToken = '';
  private duplicatedPage: any;

  public sortType = [{ type: 'Latest' }, { type: 'Oldest' }];
  public defaultSort = 'Latest';
  public activeDomainList = [];
  public targetPageData = {};
  storeInfo$ = this.storeInfoService.getStoreInfos();
  public themeType = 'site';

  @ViewChild('linkList') linkList: ElementRef;

  private static getDismissReason(reason: any): string {
    if (reason === ModalDismissReasons.ESC) {
      return 'by pressing ESC';
    } else if (reason === ModalDismissReasons.BACKDROP_CLICK) {
      return 'by clicking on a backdrop';
    } else {
      return `with: ${reason}`;
    }
  }

  ngOnInit(): void {
    this.bearer = localStorage.getItem('bearer');
    this.storeId = localStorage.getItem('store_id');
    this.getPageListing();
    this.getStoreInfo();
  }

  private getPageListing(): void {
    this.loading = true;
    this.svc.getPageList(this.bearer, this.storeId).subscribe((res) => {
      let me = this;
      // The home page is managed from Website > Design, not here. It has no public
      // slug of its own — it answers on the storefront's home route — so listing it
      // among ordinary pages offers an address and actions that don't apply to it.
      me.pageList = (res['data'] ?? []).filter(
        (page: any) => +page?.is_home !== 1
      );
      if (me.pageList.length <= 0) {
        this.loading = false;
      } else {
        this.loading = false;
        this.displayedPageList = me.pageList.reverse();
        this.updateFilter();
      }
    });
  }

  private getDomainList() {
    this.svc.getDomainList(this.bearer, this.storeId).subscribe(
      (res) => {
        const activeDomainList = res['data']
          .filter((domain) => +domain.status === 2)
          .map((domain) => `https://${domain?.domain_name}`);

        activeDomainList.unshift(this.storeUrl);

        this.activeDomainList = activeDomainList;
      },
      (error) => {},
      () => {}
    );
  }

  private getStoreInfo(): void {
    this.storeInfo$.subscribe((res) => {
      this.storeUrl = res.store_url;
      this.getDomainList();
    });
  }

  private deletePage(id: number): void {
    this.loading = true;
    const body = {
      status: 0,
      store_id: this.storeId,
      store_page_id: id,
    };
    this.svc.updatePage(this.bearer, body).subscribe(
      (res) => {
        setTimeout(() => {
          this.utils.notify.showSuccess(
            this.utils.translate('Deleted Successfully!')
          );
          this.getPageListing();
        }, 2000);
      },
      (error) => {
        console.log(error);
        this.utils.notify.showError(this.utils.translate('Delete failed!'));
      }
    );
  }

  public edit(id: number): void {
    this.router.navigateByUrl('/dashboard/page/edit/' + id);
  }

  public viewReport(data: any): void {
    const searchTarget = data?.page_url || data?.page_title || '';
    this.router.navigate(['/dashboard/landing-page-report'], {
      queryParams: searchTarget ? { search: searchTarget } : {},
    });
  }

  /**
   * Whether this page has taken over the storefront's home route. Only one page
   * per store can be one; the backend clears the flag from the rest on save.
   */
  public isHomePageRow(data: any): boolean {
    return +data?.is_home === 1;
  }

  /**
   * Where a page is published. A home page replaces the storefront's home page, so
   * it is reached at the store root — its slug exists only so it can be previewed
   * and opened in the editor.
   */
  public publicPageUrl(base: string, data: any): string {
    if (this.isHomePageRow(data)) {
      return base;
    }
    return `${base}/${this.themeType}/${data?.page_url}`;
  }

  public getEditorInfo(data: any): { key: string; label: string; icon: string; class: string } {
    const editor = (data?.editor || '').toLowerCase();
    const themeType = (data?.page_theme_type || '').toLowerCase();

    // 1. Explicit editor column from API
    if (editor === 'html' || editor === 'htmleditor' || editor === 'code') {
      return {
        key: 'html',
        label: 'HTML Editor',
        icon: 'fi fi-rr-comment-code',
        class: 'badge-editor-html',
      };
    }
    if (editor === 'classic' || editor === 'unlayer') {
      return {
        key: 'classic',
        label: 'Classic Editor',
        icon: 'fi fi-rr-layout-fluid',
        class: 'badge-editor-classic',
      };
    }
    if (editor === 'studio' || editor === 'new') {
      return {
        key: 'studio',
        label: 'Studio Editor',
        icon: 'fi fi-rr-magic-wand',
        class: 'badge-editor-studio',
      };
    }

    // 2. Fallback to page_theme_type if editor column is not set
    if (themeType === 'html') {
      return {
        key: 'html',
        label: 'HTML Editor',
        icon: 'fi fi-rr-comment-code',
        class: 'badge-editor-html',
      };
    }
    if (themeType === 'new' || themeType === 'studio') {
      return {
        key: 'studio',
        label: 'Studio Editor',
        icon: 'fi fi-rr-magic-wand',
        class: 'badge-editor-studio',
      };
    }

    // 3. Default fallback for legacy pages
    return {
      key: 'classic',
      label: 'Classic Editor',
      icon: 'fi fi-rr-layout-fluid',
      class: 'badge-editor-classic',
    };
  }

  public showLinks(data): void {
    console.log('showLinks', { data });
    if (data.editor === 'studio') {
      this.themeType = 'site';
    } else {
      this.themeType = 'page';
    }
    delete data.page_content;
    this.targetPageData = data;
    this.modalService
      .open(this.linkList, {
        size: 'lg',
        backdrop: 'static',
      })
      .result.then(
        (result) => {
          this.closeResult = `Closed with: ${result}`;
        },
        (reason) => {}
      );
  }

  public preview(baseUrl, data: any): void {
    if (data.visibility === '0') {
      if (this.previewToken === '') {
        this.svc
          .getPreviewToken(this.bearer, this.storeId, data.page_url)
          .subscribe((res) => {
            // The token comes back as "<slug>?preview_key=<key>". A hidden home
            // page is not served on the home route, so it is previewed at the
            // store root carrying the same key.
            const tokenUrl = res['data']['url'] ?? '';
            const queryIndex = tokenUrl.indexOf('?');
            this.previewToken = this.isHomePageRow(data)
              ? `${baseUrl}/${queryIndex === -1 ? '' : tokenUrl.slice(queryIndex)}`
              : `${baseUrl}/page/${tokenUrl}`;
            window.open(this.previewToken);
          });
      }
    } else {
      if (this.isHomePageRow(data)) {
        window.open(baseUrl);
      } else if (this.themeType === 'site') {
        window.open(baseUrl + '/site/' + data?.page_url);
      } else {
        window.open(baseUrl + '/page/' + data?.page_url);
      }
    }
  }

  public open(content: any, id: number): void {
    this.getPageName(id);
    this.modalService
      .open(content, {
        ariaLabelledBy: 'modal-basic-title',
        centered: true,
        backdrop: 'static',
      })
      .result.then(
        (result) => {
          if (result == 'delete') {
            this.deletePage(id);
            this.closeResult = `Closed with: ${result}`;
          }
          if (result == 'submit') {
            this.loading = true;
            this.duplicatedPage = true;
            localStorage.setItem('duplicatePage', this.duplicatedPage);
            const body = {
              store_id: this.storeId,
              store_page_id: id,
              page_title: this.duplicatePageName,
            };
            this.svc.postDuplicatePage(this.bearer, body).subscribe(
              (res) => {
                const returnStorePageID = res['data']['store_page_id'];
                this.edit(returnStorePageID);
              },
              (error) => {
                console.log(error);
                this.loading = false;
                this.utils.notify.showError(error['error']['message']);
              }
            );
          }
        },
        (reason) => {
          this.closeResult = `Dismissed ${PagesComponent.getDismissReason(
            reason
          )}`;
        }
      );
  }

  private countBase64Images(content: any): number {
    if (!content) {
      return 0;
    }
    const str = typeof content === 'string' ? content : JSON.stringify(content);
    return (str.match(/data:image\/[a-z0-9.+-]+;base64,/gi) || []).length;
  }

  public tryDuplicate(content: any, data: any): void {
    // Pages with inline base64 images must not be duplicated — duplicating them
    // multiplies the bloated content (one such page is ~56MB) and pushes the
    // page/list endpoint past the backend memory limit (500). Images must be
    // re-inserted via the image tool so they upload to the CDN first.
    const base64ImageCount = this.countBase64Images(data?.page_content);
    if (base64ImageCount > 0) {
      this.utils.notify.showError(
        base64ImageCount +
          ' ' +
          this.utils.translate(
            'inline base64 image(s) found in this page. Pages with embedded images cannot be duplicated. Please re-insert the images with the image tool so they upload to the CDN first.'
          )
      );
      return;
    }
    this.open(content, data.store_page_id);
  }

  /* PAGINATION */
  private sliceCart(data: any): string[] {
    this.start = Math.round((this.page - 1) * this.selectedPage);
    this.end = this.start + this.selectedPage;
    this.end = this.end > data.length ? data.length : this.end;
    this.end = this.end > this.start ? this.end : this.start;
    return data.slice(this.start, this.end);
  }

  private selectPage(page: any): void {
    this.selectedPage = page;
    this.page = 1;
    // this.sliceCart();
  }

  public changePage(pageOfItems: number): void {
    this.page = pageOfItems;
    this.updateFilter();
    if (this.defaultSort !== 'Latest') {
      this.displayedPageList = this.displayedPageList.reverse();
    }
    this.sliceCart(this.displayedPageList);
  }

  private getPageName(id: number): void {
    const filteredPageList = this.pageList.filter(
      (item) => item.store_page_id === id
    );
    this.pageName = filteredPageList[0].page_title;
    this.deletingHomePage = this.isHomePageRow(filteredPageList[0]);
    this.duplicatePageName = 'Copy of ' + filteredPageList[0].page_title;
  }

  clearPageSearch() {
    this.filteredString = '';
    this.updateFilter();
  }

  public updateFilter(event = null): void {
    this.displayedPageList = this.filterPageList(this.pageList, [
      this.filteredString,
    ]);

    if (this.defaultSort !== 'Latest') {
      this.displayedPageList = this.displayedPageList.reverse();
    }
  }

  private filterPageList(pageList: any, filterString: any): Array<string> {
    if (!pageList || pageList.length === 0) {
      return pageList;
    }

    return pageList.filter((page: any) => {
      // Filter by search query
      const firstString = filterString[0];
      if (typeof firstString !== 'undefined') {
        const keywordsFilter = firstString.toLowerCase().split(' ');

        return keywordsFilter.every((keyword: string) => {
          const searchHaystack = `${page.page_title}`;

          return searchHaystack.toLowerCase().indexOf(keyword) >= 0;
        });
      } else {
        return pageList;
      }
    });
  }

  public getDisplayedPageList(): any {
    if (this.displayedPageList.length > 0) {
      this.displayedPageList.forEach((value, index) => {
        this.expanded[index] = false;
      });
    }

    return this.sliceCart(this.displayedPageList);
  }

  public getSelectedSort(sortType: string): string {
    const sortSelected = this.sortType?.find(
      (c) => c.type.toLowerCase() === this.defaultSort
    );

    return sortSelected?.type ?? sortType;
  }

  public sortByType(sortType): void {
    console.log('sort By', sortType);
    if (this.defaultSort !== sortType.type) {
      this.displayedPageList = this.displayedPageList.reverse();
    }
    this.defaultSort = sortType.type;
  }
}
